import { useEffect, useState, useMemo } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { Plus, Trash, FloppyDisk, ArrowClockwise, DoorOpen, CheckCircle, XCircle, ArrowUp, ArrowDown } from "@phosphor-icons/react";

const QTYPES = [{ v: "text", l: "Free text" }, { v: "rating", l: "Rating (1-5 stars)" }, { v: "select", l: "Dropdown" }];
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);

export default function OffboardingConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const load = async () => {
    try {
      const r = await api.get("/offboarding/config");
      setCfg(r.data); setInitial(JSON.parse(JSON.stringify(r.data)));
    } catch (e) { setMsg({ type: "error", text: formatAxiosError(e) }); }
  };
  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => cfg && initial && JSON.stringify(cfg) !== JSON.stringify(initial), [cfg, initial]);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: "", text: "" }), 4000); };

  const save = async () => {
    setSaving(true);
    try {
      const eqs = cfg.exit_questions.map(q => ({
        ...q,
        key: slug(q.key) || slug(q.label),
      }));
      const r = await api.put("/offboarding/config", { exit_questions: eqs });
      setCfg(r.data); setInitial(JSON.parse(JSON.stringify(r.data)));
      flash("ok", "Saved.");
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setSaving(false); }
  };
  const reset = () => setCfg(JSON.parse(JSON.stringify(initial)));
  const addQ = () => setCfg({ ...cfg, exit_questions: [...cfg.exit_questions, { key: `q_${Date.now().toString(36)}`, label: "New question", type: "text" }] });
  const updateQ = (i, patch) => { const arr = [...cfg.exit_questions]; arr[i] = { ...arr[i], ...patch }; setCfg({ ...cfg, exit_questions: arr }); };
  const removeQ = (i) => setCfg({ ...cfg, exit_questions: cfg.exit_questions.filter((_, idx) => idx !== i) });
  const moveQ = (i, dir) => {
    const arr = [...cfg.exit_questions]; const ni = i + dir; if (ni < 0 || ni >= arr.length) return;
    [arr[i], arr[ni]] = [arr[ni], arr[i]]; setCfg({ ...cfg, exit_questions: arr });
  };

  if (!cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div>
      <div className="eyebrow flex items-center gap-2 mb-1"><DoorOpen size={12} weight="bold" /> Exit interview</div>
      <h2 className="font-display text-2xl font-black tracking-tight mb-1">Offboarding questionnaire</h2>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        Questions asked to departing employees. Use ratings for quantitative feedback, free text for open
        answers, or dropdowns for structured responses.
      </p>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border flex items-center gap-2 ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`}>
          {msg.type === "ok" ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
          {msg.text}
        </div>
      )}

      <div className="space-y-2 mb-4">
        {cfg.exit_questions.map((q, i) => (
          <div key={q.key + i} className="border border-border p-3" data-testid={`exit-q-row-${q.key}`}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
              <div className="md:col-span-5">
                <label className="eyebrow block mb-1">Question</label>
                <input value={q.label} onChange={e => updateQ(i, { label: e.target.value })} className="w-full border border-border px-2 py-1.5 text-sm" />
              </div>
              <div className="md:col-span-3">
                <label className="eyebrow block mb-1">Key</label>
                <input value={q.key} onChange={e => updateQ(i, { key: slug(e.target.value) })} className="w-full border border-border px-2 py-1.5 text-sm font-mono-mini" />
              </div>
              <div className="md:col-span-3">
                <label className="eyebrow block mb-1">Type</label>
                <select value={q.type} onChange={e => updateQ(i, { type: e.target.value })} className="w-full border border-border px-2 py-1.5 text-sm">
                  {QTYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div className="md:col-span-1 flex items-end justify-end gap-1">
                <button onClick={() => moveQ(i, -1)} disabled={i === 0} className="border border-border p-1 hover:bg-muted disabled:opacity-30"><ArrowUp size={10} weight="bold" /></button>
                <button onClick={() => moveQ(i, 1)} disabled={i === cfg.exit_questions.length - 1} className="border border-border p-1 hover:bg-muted disabled:opacity-30"><ArrowDown size={10} weight="bold" /></button>
                <button onClick={() => removeQ(i)} className="border border-[#FF3B30] text-[#FF3B30] p-1 hover:bg-red-50"><Trash size={10} weight="bold" /></button>
              </div>
            </div>
            {q.type === "select" && (
              <div className="mt-2">
                <label className="eyebrow block mb-1">Options (one per line, format: value|label)</label>
                <textarea
                  value={(q.options || []).map(o => `${o.value}|${o.label}`).join("\n")}
                  onChange={e => {
                    const opts = e.target.value.split("\n").map(l => {
                      const [v, ...rest] = l.split("|");
                      return { value: (v || "").trim(), label: (rest.join("|") || v || "").trim() };
                    }).filter(o => o.value);
                    updateQ(i, { options: opts });
                  }}
                  rows={3}
                  className="w-full border border-border px-2 py-1.5 text-xs font-mono"
                />
              </div>
            )}
          </div>
        ))}
        <button onClick={addQ} className="border border-dashed border-border px-3 py-2 text-sm hover:bg-muted w-full flex items-center justify-center gap-1" data-testid="add-exit-question">
          <Plus size={14} weight="bold" /> Add question
        </button>
      </div>

      <div className="flex items-center gap-2 justify-end">
        <button onClick={reset} disabled={!dirty} className="border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-40 flex items-center gap-2">
          <ArrowClockwise size={14} weight="bold" /> Reset
        </button>
        <button onClick={save} disabled={!dirty || saving} className="btn-primary flex items-center gap-2 disabled:opacity-40" data-testid="save-offboarding-config">
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
